// Design frames: hifi 04 (H4.1 agent Today, H4.2 dark), wireframes 14 (14.7 manager, 14.9 owner), hifi 10 (H10.1 phone, H10.2 desktop).
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card,
  DataTable,
  Icon,
  List,
  ListItem,
  Notice,
  Segmented,
  SIDE_SHEET_QUERY,
  Skeleton,
  StatTile,
  iconChevron,
  useMediaQuery,
} from '@sauda/ui/kit';
import { Button } from '@sauda/ui/kit';
import { Screen } from '../app/chrome.js';
import { LinkButton } from '../app/LinkButton.js';
import { NewMenu } from '../app/NewMenu.js';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import {
  rankBranches,
  sumCells,
  thingsToday,
  type Attention,
  type Overview,
  type Totals,
} from '../lib/dashboard.js';
import { formatRupees } from '../lib/money.js';
import { OwingList, type OwingLine } from './OwingList.js';
import type { Branch } from '../lib/types.js';
import { formatIstToday } from '../lib/time.js';
import {
  DealsCard,
  FollowUpsCard,
  MoneyCard,
  AllClear,
  BrokerageDueCard,
  RenewalsCard,
  RequestsCard,
  SectionCard,
  UnassignedCard,
  VisitsTodayCard,
  useTodayQuery,
} from './today/parts.js';

function Hero({ total, parts }: { total: number; parts: { label: string; n: number }[] }) {
  const { t } = useTranslation();
  return (
    <Card hero>
      <p className="k-t-label k-muted">{formatIstToday()}</p>
      <div className="hero-line">
        <p className="k-t-display-xl hero-number">{total}</p>
        <p className="k-t-body-lg">{t('today.thingsNeedYou', { count: total })}</p>
      </div>
      <div className="chips-row">
        {parts
          .filter((p) => p.n > 0)
          .map((p) => (
            <span key={p.label} className="hero-chip">
              {p.label}
            </span>
          ))}
      </div>
    </Card>
  );
}

function AgentToday({ team, manager }: { team: boolean; manager: boolean }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const today = useTodayQuery(true, auth.agencyId);
  const attention = useQuery({
    queryKey: ['attention', auth.agencyId],
    queryFn: () => api<Attention>('/dashboard/attention'),
    enabled: manager,
  });
  const overview = useQuery({
    queryKey: ['overview', auth.agencyId, 30],
    queryFn: () => api<Overview>('/dashboard?days=30'),
    enabled: manager && auth.allowed('passbook.read'),
  });
  const money = useQuery({
    queryKey: ['passbook-summary', auth.agencyId],
    queryFn: () => api<Totals>('/passbook/summary'),
    enabled: auth.allowed('passbook.read'),
  });
  const branches = useQuery({
    queryKey: ['branches', auth.agencyId],
    queryFn: () => api<Branch[]>('/branches'),
    enabled: auth.allowed('branches.view'),
  });
  const owing = useQuery({
    queryKey: ['owing', auth.agencyId],
    queryFn: () => api<OwingLine[]>('/passbook/owing'),
    enabled: auth.allowed('passbook.read'),
  });
  const branchName = branches.data?.find((b) => b.id === auth.current?.branchId)?.name;
  const things = today.data === undefined ? undefined : thingsToday(today.data);
  const unassigned = attention.data?.unassignedVisits.count ?? 0;
  return (
    <Screen
      title={t('nav.today')}
      large
      wide
      eyebrow={[auth.current?.agencyName, branchName, manager ? t('roles.branch_manager') : null]
        .filter(Boolean)
        .join(' · ')}
    >
      {manager ? null : today.isPending ? (
        <Skeleton height={168} />
      ) : things === undefined ? null : (
        <Hero
          total={things.total + unassigned}
          parts={[
            {
              label: t('today.visitsChip', { count: things.visits + unassigned }),
              n: things.visits + unassigned,
            },
            { label: t('today.dealsChip', { count: things.deals }), n: things.deals },
            { label: t('today.renewalsChip', { count: things.renewals }), n: things.renewals },
          ]}
        />
      )}
      {manager ? <UnassignedCard query={attention} /> : null}
      {manager && overview.data !== undefined ? (
        <section aria-label={t('today.branchMonth')} className="today-section">
          <Card>
            <h2 className="k-t-title-sm">
              {t('today.branchThisMonth', { branch: branchName ?? t('today.branchMonth') })}
            </h2>
            {(() => {
              const sum = sumCells(overview.data.branches);
              return (
                <div className="stat-grid">
                  <StatTile tone="highlight" label={t('today.visitsBooked')} value={sum.visits} />
                  <StatTile label={t('today.dealsStarted')} value={sum.started} />
                  <StatTile label={t('today.received')} value={formatRupees(sum.received)} />
                  <StatTile label={t('today.renewalsDue')} value={sum.renewalsDue} />
                </div>
              );
            })()}
            <ul className="k-sr-only">
              {overview.data.branches.map((b) => (
                <li key={b.branchId}>{b.branchName}</li>
              ))}
            </ul>
            <LinkButton variant="text" to="/passbook">
              {t('today.openDashboard')}
            </LinkButton>
          </Card>
        </section>
      ) : null}
      <FollowUpsCard query={today} />
      <RequestsCard query={today} />
      <VisitsTodayCard query={today} />
      <DealsCard query={today} />
      <RenewalsCard query={today} />
      <BrokerageDueCard query={today} />
      {auth.allowed('passbook.read') ? (
        <section aria-label={t('owing.title')} className="today-section">
          <h2 className="k-t-title-sm">{t('owing.title')}</h2>
          <OwingList limit={5} />
        </section>
      ) : null}
      <AllClear
        today={today.data}
        unassigned={unassigned}
        waiting={(attention.data?.waitingRequests.count ?? 0) + (owing.data?.length ?? 0)}
        ready={!manager || attention.isSuccess}
      />
      {money.data === undefined ? null : (
        <MoneyCard
          received={money.data.receivedPaise}
          due={money.data.duePaise}
          overdue={money.data.overduePaise}
        />
      )}
      <div className="fab-slot fab-slot--phone">
        <NewMenu team={team} />
      </div>
    </Screen>
  );
}

const PERIODS = [30, 90, 365] as const;

function OwnerToday() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState<number>(30);
  const overview = useQuery({
    queryKey: ['overview', auth.agencyId, days],
    queryFn: () => api<Overview>(`/dashboard?days=${days}`),
  });
  const attention = useQuery({
    queryKey: ['attention', auth.agencyId],
    queryFn: () => api<Attention>('/dashboard/attention'),
  });
  const todayData = useTodayQuery(true, auth.agencyId);
  const sum = overview.data === undefined ? null : sumCells(overview.data.branches);
  const sections: { key: keyof Attention; title: string; to: string; line: string }[] = [
    { key: 'waitingRequests', title: t('today.requestsWaiting'), to: '/visits', line: 'visits' },
    { key: 'overdueBrokerage', title: t('today.overdueBrokerage'), to: '/passbook', line: 'deals' },
    { key: 'unassignedVisits', title: t('today.unassignedOld'), to: '/visits', line: 'visits' },
    { key: 'waitingMemos', title: t('today.memosWaiting'), to: '/deals', line: 'deals' },
    {
      key: 'brokerageDueSoon',
      title: t('today.brokerageThisWeek'),
      to: '/passbook',
      line: 'deals',
    },
    {
      key: 'overdueRenewals',
      title: t('today.renewalsOverdue'),
      to: '/renewals',
      line: 'renewals',
    },
    {
      key: 'renewalsSoon',
      title: t('today.renewalsNext30'),
      to: '/renewals',
      line: 'renewals',
    },
  ];
  const countLine = (kind: string, count: number): string =>
    kind === 'visits'
      ? t('today.visitsCount', { count })
      : kind === 'renewals'
        ? t('today.renewalsCount', { count })
        : t('today.dealsCount', { count });
  const branches = overview.data === undefined ? [] : rankBranches(overview.data.branches);
  const desktop = useMediaQuery(SIDE_SHEET_QUERY);
  const attentionList = (
    <SectionCard title="" query={attention}>
      {(d) => {
        const shown = sections.filter((x) => d[x.key].count > 0);
        return shown.length === 0 ? (
          <Card>
            <p className="k-t-body k-muted">{t('today.nothingDue')}</p>
          </Card>
        ) : (
          <List label={t('today.needsAttention')}>
            {shown.map((x) => (
              <ListItem
                key={x.key}
                title={x.title}
                supporting={countLine(x.line, d[x.key].count)}
                trailing={<Icon icon={iconChevron} />}
                onClick={() => void navigate(x.to)}
              />
            ))}
          </List>
        );
      }}
    </SectionCard>
  );
  const heroBlock = overview.isPending ? (
    <Skeleton height={168} />
  ) : overview.isError ? (
    <Notice
      tone="error"
      action={
        <Button variant="text" onClick={() => void overview.refetch()}>
          {t('app.retry')}
        </Button>
      }
    >
      {t('today.loadFailed')}
    </Notice>
  ) : sum === null ? null : (
    <div className="owner__top">
      <Card hero>
        <p className="k-t-label-sm">{t('today.receivedDays', { days })}</p>
        <p className="k-t-display-xl hero-number" data-testid="owner-received">
          {formatRupees(sum.received)}
        </p>
        <p className="k-t-body">
          {t(desktop ? 'today.stillDue' : 'today.dueClosed', {
            amount: formatRupees(sum.due),
            closed: sum.closed,
          })}
        </p>
      </Card>
      {desktop ? (
        <div className="stat-grid owner__stats">
          <StatTile label={t('today.visitsBooked')} value={sum.visits} />
          <StatTile label={t('today.dealsStarted')} value={sum.started} />
          <StatTile label={t('today.renewalsDue')} value={sum.renewalsDue} />
          <StatTile label={t('today.renewalsRecovered')} value={sum.recovered} />
        </div>
      ) : null}
    </div>
  );
  const newMenu = (
    <div className="fab-slot fab-slot--phone">
      <NewMenu team />
    </div>
  );
  if (!desktop) {
    return (
      <Screen
        title={t('nav.today')}
        large
        eyebrow={`${auth.current?.agencyName ?? ''} · ${t('today.allBranches')}`}
        className="owner"
      >
        {heroBlock}
        <FollowUpsCard query={todayData} />
        <section aria-label={t('today.needsAttention')} className="today-section">
          <h2 className="k-t-title-sm">{t('today.needsAttention')}</h2>
          {attentionList}
        </section>
        {overview.data === undefined ? null : (
          <section aria-label={t('today.branches')} className="today-section">
            <h2 className="k-t-title-sm">{t('today.branchesByReceived')}</h2>
            <List label={t('today.branches')}>
              {branches.map((b) => (
                <ListItem
                  key={b.branchId}
                  title={b.branchName}
                  supporting={t('today.branchLine', {
                    received: formatRupees(b.brokerageReceivedPaise),
                    due: formatRupees(b.brokerageDuePaise),
                  })}
                />
              ))}
            </List>
          </section>
        )}
        {newMenu}
      </Screen>
    );
  }
  return (
    <Screen
      title={auth.current?.agencyName ?? t('nav.today')}
      large
      eyebrow={t('today.allBranches')}
      className="owner"
    >
      <div className="owner__grid">
        <div className="owner__main">
          <div className="owner__period">
            <Segmented
              label={t('today.period')}
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
              options={PERIODS.map((d) => ({
                value: String(d),
                label: t('today.periodDays', { count: d }),
              }))}
            />
            <LinkButton variant="outlined" to="/passbook">
              {t('today.export')}
            </LinkButton>
            <div className="owner__new">
              <NewMenu team compact labelled below />
            </div>
          </div>
          {heroBlock}
          {overview.data === undefined ? null : (
            <section aria-label={t('today.branches')} className="today-section owner__branches">
              <h2 className="k-t-title">{t('today.branchesTitle')}</h2>
              <DataTable
                label={t('today.branches')}
                rows={branches}
                rowKey={(b) => b.branchId}
                columns={[
                  { key: 'name', header: t('today.colBranch'), render: (b) => b.branchName },
                  { key: 'visits', header: t('today.colVisits'), render: (b) => b.visitsBooked },
                  { key: 'started', header: t('today.colStarted'), render: (b) => b.dealsStarted },
                  { key: 'closed', header: t('today.colClosed'), render: (b) => b.dealsClosed },
                  {
                    key: 'due',
                    header: t('today.colDue'),
                    render: (b) => formatRupees(b.brokerageDuePaise),
                  },
                  {
                    key: 'received',
                    header: t('today.colReceived'),
                    render: (b) => formatRupees(b.brokerageReceivedPaise),
                  },
                  {
                    key: 'rdue',
                    header: t('today.colRenewalsDue'),
                    render: (b) => b.renewalsDue60,
                  },
                  {
                    key: 'rrec',
                    header: t('today.colRecovered'),
                    render: (b) => b.renewalsRecovered,
                  },
                ]}
              />
            </section>
          )}
        </div>
        <aside className="owner__side" aria-label={t('today.needsAttention')}>
          <FollowUpsCard query={todayData} />
          <h2 className="k-t-title-lg">{t('today.needsAttention')}</h2>
          {attentionList}
        </aside>
      </div>
    </Screen>
  );
}

/** Today by role: an agent's day, a manager's branch, an owner's pulse. Accounts have Money instead. */
export function TodayScreen() {
  const auth = useAuth();
  const role = auth.current?.role;
  if (role === 'owner') return <OwnerToday />;
  return <AgentToday team={role === 'branch_manager'} manager={role === 'branch_manager'} />;
}
